import { PromiseToPay } from "@portal/BaseComponents/Activites/PromiseToPay/PromiseToPay";
import { RaiseDispute } from "@portal/BaseComponents/Activites/RaiseADispute/RaiseDispute";
import { DownloadDoc } from "@portal/BaseComponents/Document/DownloadDoc/DownloadDoc";
import { Flex } from "@portal/BaseComponents/Flex/Flex";
import { HideWrapper } from "@portal/BaseComponents/HideWrapper/HideWrapper";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import { FormHeader } from "@portal/BaseComponents/UI/FormArea/FormHeader";
import { useGetInvoiceStatusesConfig } from "@portal/Components/Portal/PotalTabs/Invoice/useInvoiceStatusesConfig";
import { usePrivileges } from "@portal/hooks/use-privilleges";
import { Icon } from "@portal/lib/icons/icons";
import { AppPageParams } from "@portal/lib/router/constants/page-info";
import { usePageNavigate } from "@portal/lib/router/hooks/usePageNavigae";
import { useGetInvoiceDetailsQuery } from "@portal/queries/invoice-query";
import { InvoiceStatus } from "@portal/types/entities/invoice";
import { Fragment } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { InvoiceDetailsComp } from "./Details/InvoiceDetails";
import { InvoiceDetailsViewProps } from "./type";
import { ScrollDrawer } from "@portal/BaseComponents/Drawer/ScrollDrawer";
import { DRAWER_ZINDEX } from "@portal/BaseComponents/Drawer/predicate";

const InvoiceIcon = (
  <Icon icon={["fal", "file-invoice"]} color="var(--primary-6)" />
);
const t = {
  title: "Invoice Details",
};
function InvoiceDetailsView(props: InvoiceDetailsViewProps) {
  const {
    open,
    header,
    headerTitle = t.title,
    actionButton,
    footer,
    onClose,
    id,
    onOpenChange,
  } = props;

  const invoiceStatusConfig = useGetInvoiceStatusesConfig();
  const [searchParams, setParams] = useSearchParams();
  const { data, isLoading } = useGetInvoiceDetailsQuery(
    searchParams.get("invoice_id")?.toString() || id
  );

  const navigate = usePageNavigate();

  const { getDisputePrivilege, getPTPPrivilege } = usePrivileges();
  const { customer_id } = useParams<AppPageParams>();
  function getParams() {
    return searchParams.get("invoice_id") ? true : false;
  }

  function handleClose() {
    onClose?.();

    if (!getParams() && customer_id) {
      navigate({
        appPage: "INVOICES",
        pathParams: {
          customer_id,
        },
      });
    } else {
      setParams();
    }
  }

  const Download = data?.attachment_id ? (
    <DownloadDoc id={data.attachment_id} name={`Invoice-${data.number}-pdf`} />
  ) : null;
  const PTP = (
    <HideWrapper
      hide={
        invoiceStatusConfig.CLOSED.includes(data?.status as InvoiceStatus) ||
        !getPTPPrivilege("PTP_CREATE")
      }
    >
      <PromiseToPay
        iconOnly
        iconColor="var(--primary-7)"
        showToolTip
        onOpenChange={onOpenChange}
        defaultInvoice={data ? [data] : []}
        key={[data].length}
        buttonProps={{
          loading: isLoading,
        }}
      />
    </HideWrapper>
  );
  const Dispute = (
    <HideWrapper
      hide={
        invoiceStatusConfig.CLOSED.includes(data?.status as InvoiceStatus) ||
        !getDisputePrivilege("DISPUTE_CREATE")
      }
    >
      <RaiseDispute
        iconOnly
        iconColor="var(--primary-7)"
        showToolTip
        onOpenChange={onOpenChange}
        defaultInvoices={data ? [data] : []}
        buttonProps={{
          loading: isLoading,
        }}
      />
    </HideWrapper>
  );
  const HeaderTitle = (
    <Flex align="center" justify="center" space="16">
      {InvoiceIcon}
      <TypographyText style={{ fontWeight: "var(--fs-regular)" }} fs="16">
        {headerTitle}
      </TypographyText>
    </Flex>
  );
  const ActionArea = (
    <Flex>
      {Dispute}
      {PTP}
      {Download}
      {actionButton?.map((action) => (
        <Fragment key={action.id}>{action.component}</Fragment>
      ))}
    </Flex>
  );
  const Header = header ? (
    header
  ) : (
    <FormHeader
      mode="drawer"
      onClose={handleClose}
      title={HeaderTitle}
      extra={ActionArea}
    />
  );

  return (
    <ScrollDrawer
      open={getParams() || open}
      title={Header}
      footer={footer}
      width={650}
      mask={true}
      maskClosable
      onClose={handleClose}
      getContainer={false}
      destroyOnClose
      design="growfin_design_default"
      styles={{
        wrapper: {
          background: "var(--gray-1)",
          boxShadow: "var(--shadow-4)",
          height: "var(--drawer-height)",
        },
        mask: {
          position: "fixed",
          inset: 0,
        },
      }}
      zIndex={DRAWER_ZINDEX}
    >
      <InvoiceDetailsComp loading={isLoading} data={data} />
    </ScrollDrawer>
  );
}

export { InvoiceDetailsView };
